// Generic, styled Excel export shared by every "Download" button in the
// app. `columns` is [{ label, width?, numFmt?, value: (row) => cell }] so
// callers only describe the shape of their table — the styling (jade
// header, borders, frozen header row) is applied once, here, so every
// export looks consistent.
import ExcelJS from 'exceljs';

// Exported so other writers stay visually identical to this one. The EOM
// financial report needs section blocks and derived rows, which the flat
// downloadXlsx shape below can't express, so it drives ExcelJS directly —
// but it pulls its palette from here. Add a writer, not a second palette.
export const JADE = 'FF0F8E6F';
export const HEADER_TEXT = 'FFFFFFFF';
export const BORDER_COLOR = 'FFD5D3CD';
export const THIN_BORDER = { style: 'thin', color: { argb: BORDER_COLOR } };

// Shared number formats so money and percentages read the same everywhere.
export const NUM_FMT = {
  money0: '"A$"#,##0',
  money2: '"A$"#,##0.00',
  pct1: '0.0%',
};

function addStyledSheet(wb, sheetName, columns, rows) {
  const ws = wb.addWorksheet(sheetName.slice(0, 31)); // Excel sheet-name limit

  ws.columns = columns.map(c => ({ header: c.label, width: c.width || 14 }));

  rows.forEach(row => {
    ws.addRow(columns.map(c => c.value(row)));
  });

  // Header row — jade fill, white bold text, thin border.
  const headerRow = ws.getRow(1);
  headerRow.height = 20;
  headerRow.eachCell(cell => {
    cell.font = { bold: true, color: { argb: HEADER_TEXT } };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: JADE } };
    cell.alignment = { vertical: 'middle' };
    cell.border = { top: THIN_BORDER, left: THIN_BORDER, bottom: THIN_BORDER, right: THIN_BORDER };
  });

  // Body rows — thin border on every cell, plus per-column number format.
  ws.eachRow((row, rowNumber) => {
    if (rowNumber === 1) return;
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.border = { top: THIN_BORDER, left: THIN_BORDER, bottom: THIN_BORDER, right: THIN_BORDER };
      const numFmt = columns[colNumber - 1]?.numFmt;
      if (numFmt) cell.numFmt = numFmt;
    });
  });

  ws.views = [{ state: 'frozen', ySplit: 1 }];
}

// Exported alongside the palette above so other writers reuse this exact
// blob/anchor/revoke dance instead of re-implementing it.
export async function downloadWorkbook(filename, wb) {
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// Single-sheet export — the common case.
export async function downloadXlsx(filename, sheetName, columns, rows) {
  const wb = new ExcelJS.Workbook();
  addStyledSheet(wb, sheetName, columns, rows);
  await downloadWorkbook(filename, wb);
}

// Multi-sheet export — for pages with more than one distinct table
// (e.g. Sales: Top Products + VIP Orders) that belong in one workbook.
// `sheets` is [{ name, columns, rows }].
export async function downloadXlsxSheets(filename, sheets) {
  const wb = new ExcelJS.Workbook();
  sheets.forEach(s => addStyledSheet(wb, s.name, s.columns, s.rows));
  await downloadWorkbook(filename, wb);
}
